import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Grid,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Print,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Work,
  School,
  Favorite,
  FamilyRestroom,
  Psychology
} from '@mui/icons-material';
import { clienteService } from '../../services/api';

interface Cliente {
  id: number;
  nome: string;
  cpf?: string;
  email?: string;
  celular?: string;
  telefone?: string;
  dataNascimento?: string;
  genero?: string;
  estadoCivil?: string;
  profissao?: string;
  escolaridade?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  cep?: string;
  uf?: string;
  diaAtendimento?: string;
  horaAtendimento?: string;
  valorAtendimento?: number;
  tipoCliente?: string;
  foto?: string;
  rg?: string;
  ocupacao?: string;
  
  // Filiação - Pais
  nomePai?: string;
  idadePai?: number;
  ocupacaoPai?: string;
  escolaridadePai?: string;
  
  nomeMae?: string;
  idadeMae?: number;
  ocupacaoMae?: string;
  escolaridadeMae?: string;
  
  // Filiação - Cônjuge
  nomeConjuge?: string;
  idadeConjuge?: number;
  ocupacaoConjuge?: string;
  escolaridadeConjuge?: string;
  profissaoConjuge?: string;
  telefoneConjuge?: string;
  
  // Anamnese - Perguntas principais
  queixaPrincipal?: string;
  mudancasPeriodo?: string;
  sintomasSinais?: string;
  acontecimentoMarcante?: string;
  relacionamentoFamiliar?: string;
  historiaDoenca?: string;
  historicoFamiliar?: string;
  medicamentosUso?: string;
  alergias?: string;
  cirurgias?: string;
  hospitalizacoes?: string;
  
  // Anamnese - Campos booleanos (Sim/Não)
  abusoAlcoolSn?: boolean;
  algumaDoencaSn?: boolean;
  familiarDoencaMentalSn?: boolean;
  tentativaSuicidioSn?: boolean;
  tratamentoPsicologicoSn?: boolean;
  usoMedicamentosSn?: boolean;
  
  // Anamnese - Detalhes dos campos booleanos
  abusoAlcoolDetalhes?: string;
  algumaDoencaDetalhes?: string;
  familiarDoencaMentalDetalhes?: string;
  tentativaSuicidioDetalhes?: string;
  tratamentoPsicologicoDetalhes?: string;
  usoMedicamentosDetalhes?: string;
  
  // Outras Informações
  emergenciaLigarPara?: string;
  emergenciaFone?: string;
  resideCom?: string;
  filhos?: string;
  outrasObs?: string;
  obsGeral?: string;
}

const ClienteFicha: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      carregarCliente();
    }
  }, [id]);

  // Na função carregarCliente, adicione mais logs:
  const carregarCliente = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [DEBUG] Carregando cliente ID:', id);
      const response = await clienteService.buscarPorId(id!);
      console.log('🔍 [DEBUG] Resposta completa:', response);
      console.log('🔍 [DEBUG] Resposta.data:', response.data);
      console.log('🔍 [DEBUG] Resposta.data.data:', response.data?.data);
      
      // O backend retorna {success, data, message}
      const dadosCliente = response.data?.data || response.data;
      console.log('🔍 [DEBUG] Dados do cliente processados:', dadosCliente);
      
      if (dadosCliente) {
        // Verificar se os campos específicos estão chegando
        console.log('🔍 [DEBUG] Campos específicos:');
        console.log('- nome_pai:', dadosCliente.nome_pai);
        console.log('- nome_mae:', dadosCliente.nome_mae);
        console.log('- queixa_principal:', dadosCliente.queixa_principal);
        console.log('- emergencia_ligar_para:', dadosCliente.emergencia_ligar_para);
        
        // ... resto do mapeamento
      }
    } catch (error) {
      console.error('❌ Erro ao carregar cliente:', error);
      setError('Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data?: string) => {
    if (!data) return 'Não informado';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const formatarValor = (valor?: number) => {
    if (!valor) return 'Não informado';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const handleImprimir = () => {
    // Remove todos os elementos que não devem aparecer na impressão
    const printButton = document.querySelector('[data-print-button]');
    if (printButton) {
      (printButton as HTMLElement).style.display = 'none';
    }
    
    // Aplica estilos específicos para impressão
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    // Imprime apenas o conteúdo
    window.print();
    
    // Restaura o botão após a impressão
    setTimeout(() => {
      if (printButton) {
        (printButton as HTMLElement).style.display = 'flex';
      }
    }, 1000);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cliente) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Cliente não encontrado'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2, 
      maxWidth: '210mm', 
      margin: '0 auto',
      minHeight: '297mm', // A4 height
      '@media print': { 
        p: '10mm',
        margin: 0,
        boxShadow: 'none',
        maxWidth: 'none',
        width: '210mm',
        minHeight: '297mm'
      } 
    }}>
      {/* Botão Imprimir - oculto na impressão */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mb: 2,
        '@media print': { display: 'none' } 
      }}>
        <Button 
          variant="contained" 
          onClick={handleImprimir} 
          startIcon={<Print />}
          sx={{ 
            '@media print': { display: 'none !important' }
          }}
        >
          Imprimir
        </Button>
      </Box>

      {/* Papel da ficha */}
      <Paper sx={{ 
        p: 3, 
        '@media print': { 
          boxShadow: 'none', 
          p: '5mm',
          margin: 0,
          borderRadius: 0
        } 
      }}>
        {/* Cabeçalho da ficha */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main',
            '@media print': {
              fontSize: '1.8rem',
              color: '#000'
            }
          }}>
            FICHA DO CLIENTE
          </Typography>
          <Divider sx={{ my: 2 }} />
        </Box>

        {/* Foto e dados principais */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Avatar
              src={cliente.foto}
              sx={{ 
                width: 150, 
                height: 150, 
                mx: 'auto', 
                mb: 2,
                border: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              <Person sx={{ fontSize: 80 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              {cliente.nome}
            </Typography>
            <Chip 
              label={cliente.tipoCliente || 'Individual'} 
              color="primary" 
              variant="outlined"
              sx={{ mb: 1 }}
            />
          </Grid>
          
          <Grid item xs={12} md={9}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              DADOS PESSOAIS
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">CPF:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {cliente.cpf || 'Não informado'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Data de Nascimento:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {formatarData(cliente.dataNascimento)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Gênero:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {cliente.genero || 'Não informado'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Estado Civil:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {cliente.estadoCivil || 'Não informado'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Profissão:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {cliente.profissao || 'Não informado'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Escolaridade:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                  {cliente.escolaridade || 'Não informado'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Dados de Contato */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
            <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
            CONTATO
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">E-mail:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.email || 'Não informado'}
              </Typography>
            </Grid>
            
            // Remover a seção de Telefone Fixo da exibição
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Celular:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.celular || 'Não informado'}
              </Typography>
            </Grid>
            
            {/* Seção removida - telefone_fixo não é mais usado */}
            
            {/* Manter apenas: */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Telefone:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente?.telefone || cliente?.celular || 'Não informado'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Endereço */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
            <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
            ENDEREÇO
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" color="text.secondary">Logradouro:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.endereco || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Número:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.numero || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Complemento:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.complemento || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Bairro:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.bairro || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Cidade:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.cidade || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">UF:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.uf || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">CEP:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.cep || 'Não informado'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Dados de Atendimento */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
            <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
            ATENDIMENTO
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Dia da Semana:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.diaAtendimento || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Horário:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.horaAtendimento || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Valor da Sessão:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {formatarValor(cliente.valorAtendimento)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Filiação */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
            <FamilyRestroom sx={{ mr: 1, verticalAlign: 'middle' }} />
            FILIAÇÃO
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Nome do Pai:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.nomePai || 'Não informado'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Nome da Mãe:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                {cliente.nomeMae || 'Não informado'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Cônjuge (se aplicável) */}
        {(cliente.nomeConjuge || cliente.profissaoConjuge || cliente.telefoneConjuge) && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
                <Favorite sx={{ mr: 1, verticalAlign: 'middle' }} />
                CÔNJUGE
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Nome:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {cliente.nomeConjuge || 'Não informado'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Profissão:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {cliente.profissaoConjuge || 'Não informado'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Telefone:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {cliente.telefoneConjuge || 'Não informado'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Anamnese */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
            <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
            ANAMNESE
          </Typography>
          
          {/* Motivo Principal */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              fontWeight: 'bold',
              fontSize: '1.1rem',
              '@media print': { color: '#000' }
            }}>Motivo Principal:</Typography>
            <Typography variant="body1" sx={{ 
              mt: 1, 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 1,
              border: '1px solid #ddd',
              '@media print': {
                bgcolor: 'transparent',
                border: '1px solid #000',
                borderRadius: 0
              }
            }}>
              {cliente.queixaPrincipal || 'Não informado'}
            </Typography>
          </Box>
          
          {/* História da Doença */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>História da Doença:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.historiaDoenca || 'Não informado'}
            </Typography>
          </Box>
          
          {/* Histórico Familiar */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Histórico Familiar:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.historicoFamiliar || 'Não informado'}
            </Typography>
          </Box>
          
          {/* Medicamentos em Uso */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Medicamentos em Uso:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.medicamentosUso || 'Não informado'}
            </Typography>
          </Box>
          
          {/* Alergias */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Alergias:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.alergias || 'Não informado'}
            </Typography>
          </Box>
          
          {/* Cirurgias */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Cirurgias:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.cirurgias || 'Não informado'}
            </Typography>
          </Box>
          
          {/* Hospitalizações */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Hospitalizações:</Typography>
            <Typography variant="body1" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {cliente.hospitalizacoes || 'Não informado'}
            </Typography>
          </Box>

          {/* Perguntas Sim/Não da Anamnese */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
            Histórico Clínico
          </Typography>
          
          <Grid container spacing={2}>
            {/* Abuso de Álcool */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Abuso de Álcool:</Typography>
                <Chip 
                  label={cliente.abusoAlcoolSn ? 'Sim' : 'Não'} 
                  color={cliente.abusoAlcoolSn ? 'warning' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.abusoAlcoolSn && cliente.abusoAlcoolDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.abusoAlcoolDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            {/* Alguma Doença */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Possui Alguma Doença:</Typography>
                <Chip 
                  label={cliente.algumaDoencaSn ? 'Sim' : 'Não'} 
                  color={cliente.algumaDoencaSn ? 'warning' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.algumaDoencaSn && cliente.algumaDoencaDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.algumaDoencaDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            {/* Familiar com Doença Mental */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Familiar com Doença Mental:</Typography>
                <Chip 
                  label={cliente.familiarDoencaMentalSn ? 'Sim' : 'Não'} 
                  color={cliente.familiarDoencaMentalSn ? 'warning' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.familiarDoencaMentalSn && cliente.familiarDoencaMentalDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.familiarDoencaMentalDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            {/* Tentativa de Suicídio */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Tentativa de Suicídio:</Typography>
                <Chip 
                  label={cliente.tentativaSuicidioSn ? 'Sim' : 'Não'} 
                  color={cliente.tentativaSuicidioSn ? 'error' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.tentativaSuicidioSn && cliente.tentativaSuicidioDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.tentativaSuicidioDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            {/* Tratamento Psicológico */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Tratamento Psicológico Anterior:</Typography>
                <Chip 
                  label={cliente.tratamentoPsicologicoSn ? 'Sim' : 'Não'} 
                  color={cliente.tratamentoPsicologicoSn ? 'info' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.tratamentoPsicologicoSn && cliente.tratamentoPsicologicoDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.tratamentoPsicologicoDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
            
            {/* Uso de Medicamentos */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Uso de Medicamentos Psiquiátricos:</Typography>
                <Chip 
                  label={cliente.usoMedicamentosSn ? 'Sim' : 'Não'} 
                  color={cliente.usoMedicamentosSn ? 'info' : 'success'} 
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                />
                {cliente.usoMedicamentosSn && cliente.usoMedicamentosDetalhes && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Detalhes: {cliente.usoMedicamentosDetalhes}
                  </Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Rodapé da ficha */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Ficha gerada em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClienteFicha;